// Plan v3 (R-73): build a one-day itinerary from Saved. Which saved things fit a day is worked out here (for the dots
// and the day cards); the times themselves come from the server when the day is built (`plan create` + `accept`), so
// opening hours, event times and clashes are checked in one place (supabase/functions/plan placeItem).
import { chooseCalendar } from './calendarChoice'
import { cleanGuests } from './calendarInvite'
import { callFn, supabase, track } from './api'
import { SUPABASE_ANON_KEY, SUPABASE_URL } from '../config'
import { newAddId } from './growth'
import type { OwnType, Plan, PlanDay, PlanItemData, PlanStop } from './plan'
import { reasonKey } from './categories'
import { browse, type Card } from './browse'
import { cachedProfile } from './v2'
import { daytimeStart, guessInHours, isFixed, partOfDay, pickSuggestions, soonestStart, textDates, wallTime, ymdIn, type DayShape } from './fit'
export { anyDay, dowOfYmd, fitsDay, lateToday, fixedDay, hoursUnknown, plusDays, setDates, textDates, todayIn, ymdIn, kmBetween, driveMin, hourIn, DAY_START_HOUR, partOfDay, isFixed, settleDay, settleInOrder, pickSuggestions, daytimeStart, type Settle, type InOrder, type InOrderMiss, type SettleStop, type DayShape } from './fit'

export { DAY_CAP, fillOrder } from './dayFill'
/**
 * How many things a filled day aims for, from the pace they picked in the first run (lead, blind test r1): relaxed 2,
 * balanced 3, packed 4; "one great thing" reads as relaxed; nothing said = balanced. Several paces: the fullest one.
 */
export function paceTarget(): number {
  // 10/15 (Alvin): pace is dropped everywhere ("the user can select as many or as few activities as they want, so the pace
  // question is kind of redundant"). A filled day aims for 3; the person adds or removes from there.
  return 3
}


// ---------- building the day on the server ----------
export type DayResult = { planId: string; skipped: { item: string; error: string }[] }
/**
 * A thing to put on the day with the time we know for it: a fixed event's own start (blind test r2, Tasha: 9:30 AM moved
 * to 10:00), a time written in the place's own text, or the start of the part of day its words name. Otherwise the
 * server picks a time inside opening hours (and the day is settled after: settleDay).
 */
/** approx: false = a real time (the event's own, or one written in the place's own words): the day is built around it.
 *  approx: true = our guess at a good hour: the server and the day settling may move it (B-144). */
/** anchor: the activity a Build around day is built around; the server never leaves it out for a suggestion (B-212). */
export type DayPick = { item: string; start?: string; approx?: boolean; anchor?: boolean }
export function pickFor(it: { id: string } & PlanItemData, ymd: string, tz: string | null, now = new Date()): DayPick {
  if (isFixed(it, tz)) return { item: it.id, start: new Date(it.start_at!).toISOString(), approx: false }
  // B-089: on today, never a time that has passed (the server applies the same floor: plan/placing.ts)
  const at = (iso: string, approx: boolean) => ({ item: it.id, start: notPast(iso, ymd, tz, now), approx })
  const t = textDates(it, now, tz)
  if (t?.hour != null && t.days.includes(ymd)) return at(wallTime(ymd, t.hour, tz), false)
  const part = partOfDay(it)
  if (part && it.kind !== 'event') return at(wallTime(ymd, guessInHours(it, ymd, part[1]), tz), true)
  // a daytime place with no stated time starts in the day, never the evening (blind test r3, Dana); the day is settled after.
  // B-144: a dinner, a bar, late drinks get no daytime start at all: the server puts them in their part of day
  const day = it.kind !== 'event' ? daytimeStart(it, ymd) : null
  if (day !== null) return at(wallTime(ymd, guessInHours(it, ymd, day), tz), true)
  return { item: it.id }
}
/** A start on today's day moved up to the soonest start when it has passed (B-089); any other day: unchanged. */
export function notPast(iso: string, ymd: string, tz: string | null, now = new Date()): string {
  if (ymd !== ymdIn(now, tz)) return iso
  const soon = soonestStart(now)
  return new Date(iso).getTime() < soon ? new Date(soon).toISOString() : iso
}

/** A new one-day itinerary with these things, timed by the server. Throws when nothing could go on it. */
/** The builder's choices for this one plan (pins 558/559/583): its group, the part(s) of day, a budget, drive minutes. */
export type DayOpts = { group?: string | null; parts?: string[]; budget?: string | null; max_min?: number | null }
export async function buildDay(ymd: string, cityLabel: string, picks: DayPick[], title?: string, o: DayOpts = {}): Promise<DayResult> {
  const extra = { ...(o.group ? { group: o.group } : {}), ...(o.parts?.length ? { parts: o.parts } : {}), ...(o.budget ? { budget: o.budget } : {}), ...(o.max_min ? { max_min: o.max_min } : {}) }
  const { plan } = await callFn<{ plan: { id: string } }>('plan', { create: { from: ymd, to: ymd, place: cityLabel, ...(title ? { title } : {}), ...extra } })
  try {
    const r = await callFn<{ skipped?: { item: string; error: string }[] }>('plan', { accept: plan.id, picks: picks.map((p) => ({ ...p, day: ymd, ...(p.start ? { approx: p.approx ?? false } : {}) })) })
    // the server logs this day as plan_created + plan_suggestion_accepted (app_events names are a registry: event_types)
    return { planId: plan.id, skipped: r.skipped ?? [] }
  } catch (e) {
    await archiveItinerary(plan.id).catch(() => {})
    throw e
  }
}
/** Put one more thing on a built day (its known time when it has one, else the server picks). */
export const addToDay = (planId: string, ymd: string, p: DayPick) => callFn<{ plan_item: { id: string } }>('plan', { add: { plan: planId, item: p.item, day: ymd, ...(p.start ? { start: p.start } : {}) } })
export const removeFromDay = (planItemId: string) => callFn('plan', { remove: planItemId })
/** R4 (itinerary_v2): a block of their own on the day, at the start and end they chose. */
export type OwnFields = { title: string; type: OwnType | null; address: string; url: string; note: string }
const ownBody = (f: OwnFields) => ({ title: f.title, address: f.address, url: f.url, type: f.type })
export const addOwnBlock = (planId: string, f: OwnFields, start: string, end: string) =>
  callFn<{ plan_item: { id: string } }>('plan', { add: { plan: planId, custom: ownBody(f), note: f.note, start, end } })
/** R4: change a block of their own (name, kind, place, link, note); its time goes through retimeStop. */
export const editOwnBlock = (planItemId: string, f: OwnFields) => callFn('plan', { edit: { id: planItemId, custom: ownBody(f), note: f.note } })
/** Change one stop's time on the day (the server keeps it inside opening hours or says why not). */
export const retimeStop = (planItemId: string, start: string, o: { end?: string; suggested?: boolean } = {}) =>
  callFn('plan', { move: { id: planItemId, start, ...(o.end ? { end: o.end } : {}), ...(o.suggested ? { suggested: true } : {}) } })

// ---------- suggestions from their own For you ranking (lead, blind tests r1-r3) ----------
const sugCache = new Map<string, Card[]>()
export type Suggestion = { card: Card; kind: 'pick' | 'nearby' }
/**
 * Things to round out a day, from the person's own For you ranking (the same browse ranking as Browse) on that day; the
 * choosing (own categories first, near the day, real gaps, widening step by step) is shared: pickSuggestions.
 */
export async function suggestFor(o: { ymd: string; center: { lat: number; lng: number } | null; city: string; tz: string | null; exclude: Set<string>; day: DayShape[]; n: number }): Promise<Suggestion[]> {
  if (o.n <= 0) return []
  const pts = o.day.filter((x) => x.lat != null && x.lng != null) as (DayShape & { lat: number; lng: number })[]
  const mid = pts.length ? { lat: pts.reduce((a, x) => a + x.lat, 0) / pts.length, lng: pts.reduce((a, x) => a + x.lng, 0) / pts.length } : o.center
  const key = `${o.ymd}|${mid ? `${mid.lat.toFixed(2)},${mid.lng.toFixed(2)}` : o.city}`
  let cards = sugCache.get(key)
  if (!cards) {
    const res = await browse({ from: o.ymd, to: o.ymd, ...(mid ? { point: mid, radius_km: 25 } : { city: o.city }) })
    const forYou = res.rows.find((r) => r.key === 'for_you')?.items ?? []
    const seen = new Set<string>()
    cards = [...forYou, ...res.rows.flatMap((r) => r.items), ...res.items].filter((c) => (seen.has(c.item.id) ? false : (seen.add(c.item.id), true)))
    sugCache.set(key, cards)
  }
  const interests = cachedProfile()?.interests ?? []
  const byId = new Map(cards.map((c) => [c.item.id, c]))
  const got = pickSuggestions(cards.map((c) => c.item), { ymd: o.ymd, tz: o.tz, exclude: o.exclude, day: o.day, n: o.n, isPick: (it) => !interests.length || reasonKey(it, interests) !== null })
  return got.map((x) => ({ card: byId.get(x.item.id)!, kind: x.kind }))
}
/** A day they backed out of before confirming leaves the Itineraries list. */
export const archiveItinerary = (planId: string) => callFn('plan', { update: { plan: planId, status: 'archived' } })

export type ItinGot = { plan: Plan & { dated?: boolean; start_date: string | null; end_date: string | null }; days: PlanDay[] }
export const getItinerary = (id: string) => callFn<ItinGot>('plan', { get: id })

// ---------- calendar: one activity at a time ----------
/**
 * The calendar add for one itinerary activity (the aha signal), straight into calendar_adds under Row Level Security,
 * with the itinerary's id and the activity's own time. Duplicate taps reuse one id per activity and method.
 */
const ADDED_KEY = 'mg_itin_cal'
type AddedMap = Record<string, { google?: boolean; ics?: boolean }>
export function readCalAdded(): AddedMap {
  try { return JSON.parse(localStorage.getItem(ADDED_KEY) ?? '{}') as AddedMap } catch { return {} }
}
function writeCalAdded(m: AddedMap) {
  try { localStorage.setItem(ADDED_KEY, JSON.stringify(m)) } catch { /* convenience only */ }
}
/**
 * Record one calendar add and wait until it has landed (blind test r1: the inserts were aborted when the .ics opened).
 * The caller opens the calendar only after this resolves. One row per activity and calendar app on this phone.
 */
export async function recordStopAdd(planId: string, s: PlanStop, method: 'google' | 'ics'): Promise<void> {
  const all = readCalAdded()
  const was = all[s.id]?.[method]
  all[s.id] = { ...all[s.id], [method]: true }
  writeCalAdded(all)
  if (was || !s.item || !s.start) return
  const end = s.end ?? new Date(new Date(s.start).getTime() + 2 * 3600e3).toISOString()
  const row = { id: newAddId(), item_id: s.item.id, method, via: 'picks', event_start: s.start, event_end: end, plan_id: planId }
  track('calendar_add', { item_id: s.item.id, method, itinerary: true })
  try {
    const { data } = await supabase.auth.getSession()
    if (!data.session) return
    const r = await fetch(`${SUPABASE_URL}/rest/v1/calendar_adds`, {
      method: 'POST', keepalive: true,
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${data.session.access_token}`, 'Content-Type': 'application/json', Prefer: 'resolution=ignore-duplicates,return=minimal' },
      body: JSON.stringify({ ...row, user_id: data.session.user.id }),
    })
    if (!r.ok) console.warn('calendar add not recorded', r.status)
  } catch { /* the calendar still opens; the add is a signal, not the event */ }
}

/** The calendar this person uses (remembered on this phone when they pick the other one); Apple first on Apple devices. */
const CAL_KEY = 'mg_cal_app'
export function calendarApp(apple: boolean): 'google' | 'ics' {
  try { const v = localStorage.getItem(CAL_KEY); if (v === 'google' || v === 'ics') return v } catch { /* default below */ }
  return apple ? 'ics' : 'google'
}
/** The calendar they told us they use on this phone, or null when we haven't asked yet (blind test r2: ask once). */
export function storedCalendarApp(): 'google' | 'ics' | null {
  try { const v = localStorage.getItem(CAL_KEY); return v === 'google' || v === 'ics' ? v : null } catch { return null }
}
/** R-24 (Round 3, Lane 3): remembered on this phone and saved on the profile (lib/calendarChoice.ts). */
export function setCalendarApp(v: 'google' | 'ics') { chooseCalendar(v) }

/**
 * Pin 596: who goes on the guest list of an itinerary's calendar events: every group member's email the group function
 * shows this person (the owner sees each member's own or host-typed email, lane D), never their own. Cleaned by
 * lib/calendarInvite.ts. Nothing is sent: the person's calendar app asks before it invites anyone.
 */
export function guestEmails(members: { me?: boolean; email?: string | null }[] | null | undefined): string[] {
  return cleanGuests((members ?? []).filter((m) => !m.me).map((m) => m.email ?? ''))
}
